import type { SmsSpendSummaryDTO } from '@today/types'

/**
 * Props for {@link SmsSpendSummary}.
 *
 * @property spend - This month's estimated spend.
 * @property monthlySpendCapEur - The cap in whole euros, or `null` for no cap.
 */
export interface SmsSpendSummaryProps {
	spend: SmsSpendSummaryDTO
	monthlySpendCapEur: number | null
}

/** `10000` → `€100.00`. */
const eurosOf = (cents: number): string => `€${(cents / 100).toFixed(2)}`

/**
 * This month's estimated SMS spend against the cap (TO-383 REQ-014), and an
 * alert when the cap is reached and SMS are paused. Pure: values in, text out.
 */
export function SmsSpendSummary({ spend, monthlySpendCapEur }: SmsSpendSummaryProps) {
	const estimate = `${eurosOf(spend.estimatedSpendCents)} estimated this month`
	return (
		<section className="flex flex-col gap-2" aria-label="SMS spend this month">
			<p className="text-ds-sm text-ds-text">
				{monthlySpendCapEur === null ? `${estimate} (no cap)` : `${estimate} of €${monthlySpendCapEur} cap`}
			</p>
			{spend.capReached ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					The monthly spend cap is reached: SMS are paused until next month or until the cap is raised.
				</p>
			) : null}
		</section>
	)
}
